import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

export interface BlogCategory {
  id?: number;
  name: string;
  description: string;
  slug: string;
  languageId: number;
  languageCode?: string;
  languageName?: string;
  isActive: boolean;
}

export interface BlogPost {
  id?: number;
  slug: string;
  title: string;
  summary: string;
  content: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  
  blogCategoryId: number;
  categoryName?: string;
  
  languageId: number;
  languageCode?: string;
  languageName?: string;
  
  authorId: number;
  authorName?: string;
  
  featuredImage: string;
  isPublished: boolean;
  isFeatured: boolean;
  publishedDate?: Date;
  viewCount?: number;
  commentCount?: number;
  tags: string[];
}

export interface Language {
  id: number;
  code: string;
  name: string;
}

export interface BlogAiGenerateRequest {
  promptId: number;
  topic: string;
  languageId: number;
  blogCategoryId: number;
  targetAudience: string;
  keywords: string;
  openAiApiKey: string;
}

@Injectable({
  providedIn: 'root'
})
export class BlogService {
  private apiUrl = environment.apiUrl + '/Blog';

  constructor(private http: HttpClient) {}

  // Categories
  getCategories(languageId?: number): Observable<BlogCategory[]> {
    const params = languageId ? { languageId: languageId.toString() } : {};
    return this.http.get<BlogCategory[]>(`${this.apiUrl}/categories`, { params });
  }

  createCategory(category: BlogCategory): Observable<BlogCategory> {
    return this.http.post<BlogCategory>(`${this.apiUrl}/categories`, category);
  }

  // Posts
  getPosts(languageId?: number, categoryId?: number, isFeatured?: boolean, page = 1, pageSize = 10, search = ''): Observable<any> {
    let params: any = { page: page.toString(), pageSize: pageSize.toString() };
    if (languageId) params.languageId = languageId.toString();
    if (categoryId) params.categoryId = categoryId.toString();
    if (isFeatured !== undefined) params.isFeatured = isFeatured.toString();
    if (search) params.search = search;
    
    return this.http.get<any>(`${this.apiUrl}/posts`, { params });
  }

  getPost(slug: string, languageId?: number): Observable<BlogPost> {
    const params = languageId ? { languageId: languageId.toString() } : {};
    return this.http.get<BlogPost>(`${this.apiUrl}/posts/${slug}`, { params });
  }

  createPost(post: BlogPost): Observable<BlogPost> {
    return this.http.post<BlogPost>(`${this.apiUrl}/posts`, post);
  }

  updatePost(id: number, post: BlogPost): Observable<BlogPost> {
    return this.http.put<BlogPost>(`${this.apiUrl}/posts/${id}`, post);
  }

  deletePost(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/posts/${id}`);
  }

  generateBlogWithAI(request: BlogAiGenerateRequest): Observable<BlogPost> {
    return this.http.post<BlogPost>(`${this.apiUrl}/generate-with-ai`, request);
  }
}
